import React, { Suspense, lazy, useEffect, useState } from 'react';
import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { Spinner } from '@/components/ui/LoadingStates';
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import ScrollToTop from "@/components/ui/ScrollToTop";
import { AuthProvider } from "@/hooks/useAuth";
import { useLanguageSync } from "@/hooks/useLanguageSync";
import { useTrackPageView } from "@/hooks/useTrackPageView";
import './lib/i18n'; // Initialize i18n
import { ErrorBoundaryDev } from '@/components/dev/ErrorBoundaryDev';
import { devUtils } from '@/lib/devUtils';
import CookieConsent from "@/components/CookieConsent";
import MaintenanceWrapper from "@/components/MaintenanceWrapper";
const Index = lazy(() => import("./pages/Index"));
const SupportChatbot = lazy(() => import("@/components/chatbot/SupportChatbot"));

// Support chatbot is only shown on the Help Center and Contact pages.
const isSupportChatRoute = (pathname: string) =>
  pathname === "/contact" || pathname === "/help" || pathname.startsWith("/help/");

const RouteScopedSupportChatbot = () => {
  const { pathname } = useLocation();
  if (!isSupportChatRoute(pathname)) return null;
  return (
    <Suspense fallback={null}>
      <SupportChatbot />
    </Suspense>
  );
};
const Auth = lazy(() => import("./pages/Auth"));
const ResetPassword = lazy(() => import("./pages/ResetPassword"));
const Profile = lazy(() => import("./pages/Profile"));
const Events = lazy(() => import("./pages/Events"));
const CreateEvent = lazy(() => import("./pages/CreateEvent"));
const EditEvent = lazy(() => import("./pages/EditEvent"));
const EventDetail = lazy(() => import("./pages/EventDetail"));
const OrganizerProfile = lazy(() => import("./pages/OrganizerProfile"));
const CategoryEvents = lazy(() => import("./pages/CategoryEvents"));
const CategoriesPage = lazy(() => import("./pages/CategoriesPage"));
const GuestSuccess = lazy(() => import("./pages/GuestSuccess"));
const AboutUs = lazy(() => import("./pages/AboutUs"));
const HowItWorks = lazy(() => import("./pages/HowItWorks"));
const Pricing = lazy(() => import("./pages/Pricing"));
const Contact = lazy(() => import("./pages/Contact"));
const TermsOfService = lazy(() => import("./pages/TermsOfService"));
const PrivacyPolicy = lazy(() => import("./pages/PrivacyPolicy"));
const CancellationPolicy = lazy(() => import("./pages/CancellationPolicy"));
const OrganizerResources = lazy(() => import("./pages/OrganizerResources"));
const EventPlanningGuide = lazy(() => import("./pages/EventPlanningGuide"));
const Analytics = lazy(() => import("./pages/Analytics"));
const Favorites = lazy(() => import("./pages/Favorites"));
const Cookies = lazy(() => import("./pages/Cookies"));
const PaymentSuccess = lazy(() => import("./pages/PaymentSuccess"));
const FeaturedPayment = lazy(() => import("./pages/FeaturedPayment"));
const FeaturedPaymentSuccess = lazy(() => import("./pages/FeaturedPaymentSuccess"));
const Billing = lazy(() => import("./pages/Billing"));
const Checkout = lazy(() => import("./pages/Checkout"));
const SubscriptionSuccess = lazy(() => import("./pages/SubscriptionSuccess"));
const SubscriptionCanceled = lazy(() => import("./pages/SubscriptionCanceled"));
const AdminAnalytics = lazy(() => import("./pages/AdminAnalytics"));
const AdminSettings = lazy(() => import("./pages/AdminSettings"));
const AdminSupport = lazy(() => import("./pages/AdminSupport"));
const Support = lazy(() => import("./pages/Support"));
const NotFound = lazy(() => import("./pages/NotFound"));
const Notifications = lazy(() => import("./pages/Notifications"));
const EventMarketing = lazy(() => import("./pages/EventMarketing"));
const OrganizerDashboard = lazy(() => import("./pages/OrganizerDashboard"));
const HelpCenter = lazy(() => import("./pages/HelpCenter"));
const HelpCenterCategory = lazy(() => import("./pages/HelpCenterCategory"));
const HelpCenterArticle = lazy(() => import("./pages/HelpCenterArticle"));
const HelpCenterSearch = lazy(() => import("./pages/HelpCenterSearch"));
const AdminTransactions = lazy(() => import("@/pages/admin/AdminTransactions"));
const AdminModeration = lazy(() => import("@/pages/admin/AdminModeration"));
const AdminAuditLogs = lazy(() => import("@/pages/admin/AdminAuditLogs"));
const AdminReports = lazy(() => import("@/pages/admin/AdminReports"));
import { AdminRoute } from "@/components/admin/AdminRoute";
import { AdminOnlyRoute } from "@/components/auth/AdminOnlyRoute";
import { DocsAccessRoute } from "@/components/auth/DocsAccessRoute";
const AdminDocsAccess = lazy(() => import("@/pages/admin/AdminDocsAccess"));
import { PremiumRoute } from "@/components/auth/PremiumRoute";
import { OrganizerRoute } from "@/components/auth/OrganizerRoute";
const AdminLayout = lazy(() => import("@/components/admin/AdminLayout"));
const DashboardBilling = lazy(() => import("./pages/DashboardBilling"));
const DashboardAnalytics = lazy(() => import("./pages/DashboardAnalytics"));
const DashboardMarketing = lazy(() => import("./pages/DashboardMarketing"));
const OrganizerWebhooks = lazy(() => import("./pages/OrganizerWebhooks"));
const AdminDashboardHome = lazy(() => import("@/pages/admin/AdminDashboardHome"));
const AdminUsers = lazy(() => import("@/pages/admin/AdminUsers"));
const AdminEvents = lazy(() => import("@/pages/admin/AdminEvents"));
const AdminTickets = lazy(() => import("@/pages/admin/AdminTickets"));
const AdminPayouts = lazy(() => import("@/pages/admin/AdminPayouts"));
const AdminSubscriptions = lazy(() => import("@/pages/admin/AdminSubscriptions"));
const AdminFeaturedPlacements = lazy(() => import("@/pages/admin/AdminFeaturedPlacements"));
const AdminAIIntelligence = lazy(() => import("@/pages/admin/AdminAIIntelligence"));
const AdminVerifications = lazy(() => import("@/pages/admin/AdminVerifications"));
const AdminTrustScores = lazy(() => import("@/pages/admin/AdminTrustScores"));
const AdminResaleFraud = lazy(() => import("@/pages/admin/AdminResaleFraud"));
const AdminBillingHealth = lazy(() => import("@/pages/admin/AdminBillingHealth"));
const AdminQuotas = lazy(() => import("@/pages/admin/AdminQuotas"));
const AdminCities = lazy(() => import("@/pages/admin/AdminCities"));
const AdminCountries = lazy(() => import("@/pages/admin/AdminCountries"));
const AdminPerformers = lazy(() => import("@/pages/admin/AdminPerformers"));
const AdminVenues = lazy(() => import("@/pages/admin/AdminVenues"));
const ArchitectureDocs = lazy(() => import("@/pages/ArchitectureDocs"));
const EmbedWidget = lazy(() => import("@/pages/EmbedWidget"));
const FeaturesDoc = lazy(() => import("@/pages/FeaturesDoc"));
const QADocs = lazy(() => import("@/pages/QADocs"));
const CityEvents = lazy(() => import("./pages/CityEvents"));
const CityHub = lazy(() => import("./pages/CityHub"));
const CitiesIndex = lazy(() => import("./pages/CitiesIndex"));
const CountryHub = lazy(() => import("./pages/CountryHub"));
const CountriesIndex = lazy(() => import("./pages/CountriesIndex"));
const CategoryCityHub = lazy(() => import("./pages/CategoryCityHub"));
const PerformerEvents = lazy(() => import("./pages/PerformerEvents"));
const ArtistsIndex = lazy(() => import("./pages/ArtistsIndex"));
const ArtistHub = lazy(() => import("./pages/ArtistHub"));
const VenuesIndex = lazy(() => import("./pages/VenuesIndex"));
const VenueHub = lazy(() => import("./pages/VenueHub"));
const OrganizersIndex = lazy(() => import("./pages/OrganizersIndex"));
const OrganizerHub = lazy(() => import("./pages/OrganizerHub"));
import { CurrencyProvider } from "@/context/CurrencyContext";
import { LocationProvider } from "@/context/LocationContext";
const LocationPrompt = lazy(() => import("@/components/LocationPrompt"));
const ManualCitySelector = lazy(() => import("@/components/ManualCitySelector"));

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      retry: 1, // Minimal retries since we handle them manually
      staleTime: 1000 * 60 * 5, // 5 minutes
      gcTime: 1000 * 60 * 15, // 15 minutes - reduced from 30
      refetchOnWindowFocus: false, // Disable refetch on focus to reduce requests
      refetchOnMount: false, // Only fetch when cache is stale
    },
  },
});

const App = () => {
  const [shouldLoadSupportChatbot, setShouldLoadSupportChatbot] = useState(false);

  React.useEffect(() => {
    if (process.env.NODE_ENV === 'development') {
      devUtils.log.info('App mounted in development mode');
      devUtils.perf.mark('app-start');
    }
  }, []);

  useEffect(() => {
    const timeoutId = window.setTimeout(() => {
      setShouldLoadSupportChatbot(true);
    }, 1500);

    return () => window.clearTimeout(timeoutId);
  }, []);

  const AppContent = () => {
    useLanguageSync();
    useTrackPageView();
    return null;
  };

  return (
    <ErrorBoundaryDev>
      <QueryClientProvider client={queryClient}>
        <AuthProvider>
          <CurrencyProvider>
            <LocationProvider>
            <TooltipProvider>
              <Toaster />
              <Sonner />
              <BrowserRouter>
                <AppContent />
                <ScrollToTop />
                <Suspense fallback={<div className="min-h-[60vh] flex items-center justify-center p-8"><Spinner size="lg" text="Loading page…" /></div>}>
                  <Routes>
                    {/* Embed widget - minimal chrome, no maintenance wrapper */}
                    <Route path="/embed/:eventId" element={<EmbedWidget />} />

                    {/* Admin routes - NOT wrapped by MaintenanceWrapper */}
                    
                    <Route path="/internal-dashboard-9351" element={<AdminRoute><AdminLayout /></AdminRoute>}>
                    <Route index element={<AdminDashboardHome />} />
                    <Route path="users" element={<AdminUsers />} />
                    <Route path="events" element={<AdminEvents />} />
                    <Route path="tickets" element={<AdminTickets />} />
                    <Route path="transactions" element={<AdminTransactions />} />
                    <Route path="payouts" element={<AdminPayouts />} />
                    <Route path="subscriptions" element={<AdminSubscriptions />} />
                    <Route path="featured" element={<AdminFeaturedPlacements />} />
                    <Route path="ai-intelligence" element={<AdminAIIntelligence />} />
                    <Route path="moderation" element={<AdminModeration />} />
                    <Route path="audit-logs" element={<AdminAuditLogs />} />
                    <Route path="reports" element={<AdminReports />} />
                    <Route path="verifications" element={<AdminVerifications />} />
                    <Route path="trust-scores" element={<AdminTrustScores />} />
                    <Route path="resale-fraud" element={<AdminResaleFraud />} />
                    <Route path="billing-health" element={<AdminBillingHealth />} />
                    <Route path="quotas" element={<AdminQuotas />} />
                    <Route path="analytics" element={<AdminAnalytics />} />
                    <Route path="settings" element={<AdminSettings />} />
                    <Route path="support" element={<AdminSupport />} />
                    <Route path="docs-access" element={<AdminDocsAccess />} />
                    <Route path="cities" element={<AdminCities />} />
                    <Route path="countries" element={<AdminCountries />} />
                    <Route path="performers" element={<AdminPerformers />} />
                    <Route path="venues" element={<AdminVenues />} />
                  </Route>
                  
                  {/* All other routes wrapped by MaintenanceWrapper */}
                  <Route path="/*" element={
                    <MaintenanceWrapper>
                      <Routes>
                        <Route path="/" element={<Index />} />
                        <Route path="/auth" element={<Auth />} />
                        <Route path="/reset-password" element={<ResetPassword />} />
                        <Route path="/profile" element={<Profile />} />
                        <Route path="/organizer-dashboard" element={<OrganizerRoute><OrganizerDashboard /></OrganizerRoute>} />
                        <Route path="/billing" element={<Billing />} />
                        <Route path="/checkout" element={<Checkout />} />
                        <Route path="/subscription-success" element={<SubscriptionSuccess />} />
                        <Route path="/subscription-canceled" element={<SubscriptionCanceled />} />
                        <Route path="/dashboard/billing" element={<OrganizerRoute><DashboardBilling /></OrganizerRoute>} />
                        <Route path="/dashboard/analytics" element={<OrganizerRoute><DashboardAnalytics /></OrganizerRoute>} />
                       <Route path="/dashboard/marketing" element={<OrganizerRoute><DashboardMarketing /></OrganizerRoute>} />
                       <Route path="/dashboard/webhooks" element={<OrganizerRoute><OrganizerWebhooks /></OrganizerRoute>} />
                        <Route path="/dashboard/events" element={<Navigate to="/organizer-dashboard" replace />} />
                        <Route path="/support" element={<Support />} />
                        <Route path="/events" element={<Events />} />
                        <Route path="/cities" element={<CitiesIndex />} />
                        <Route path="/cities/:slug" element={<CityHub />} />
                        <Route path="/countries" element={<CountriesIndex />} />
                        <Route path="/countries/:slug" element={<CountryHub />} />
                        <Route path="/discover/:categorySlug/:citySlug" element={<CategoryCityHub />} />
                        <Route path="/events/:citySlug" element={<CityEvents />} />
                        <Route path="/create-event" element={<OrganizerRoute><CreateEvent /></OrganizerRoute>} />
                        <Route path="/edit-event/:id" element={<OrganizerRoute><EditEvent /></OrganizerRoute>} />
                        <Route path="/event/:identifier" element={<EventDetail />} />
                        <Route path="/events/:categorySlug/:identifier" element={<EventDetail />} />
                        <Route path="/organizer/profile/:identifier" element={<OrganizerProfile />} />
                        <Route path="/organizer/:id" element={<OrganizerProfile />} />
                        <Route path="/performer/:slug" element={<PerformerEvents />} />
                        <Route path="/artists" element={<ArtistsIndex />} />
                        <Route path="/artists/:slug" element={<ArtistHub />} />
                        <Route path="/venues" element={<VenuesIndex />} />
                        <Route path="/venues/:slug" element={<VenueHub />} />
                        <Route path="/organizers" element={<OrganizersIndex />} />
                        <Route path="/organizers/:slug" element={<OrganizerHub />} />
                        <Route path="/event-marketing" element={<EventMarketing />} />
                        <Route path="/event-marketing/:eventId" element={<EventMarketing />} />
                        <Route path="/category/:slug" element={<CategoryEvents />} />
                        <Route path="/categories" element={<CategoriesPage />} />
                        <Route path="/favorites" element={<Favorites />} />
                        <Route path="/notifications" element={<Notifications />} />
                        <Route path="/how-it-works" element={<HowItWorks />} />
                        <Route path="/pricing" element={<Pricing />} />
                        <Route path="/contact" element={<Contact />} />
                        <Route path="/terms-of-service" element={<TermsOfService />} />
                        <Route path="/privacy-policy" element={<PrivacyPolicy />} />
                        <Route path="/cancellation-policy" element={<CancellationPolicy />} />
                        <Route path="/organizer-resources" element={<OrganizerResources />} />
                        <Route path="/event-planning-guide" element={<EventPlanningGuide />} />
                        <Route path="/analytics" element={<Analytics />} />
                        <Route path="/about" element={<AboutUs />} />
                        <Route path="/cookies" element={<Cookies />} />
                        <Route path="/payment-success" element={<PaymentSuccess />} />
                        <Route path="/featured-payment" element={<FeaturedPayment />} />
                        <Route path="/featured-payment-success" element={<FeaturedPaymentSuccess />} />
                        <Route path="/guest-success" element={<GuestSuccess />} />
                        <Route path="/help" element={<HelpCenter />} />
                        <Route path="/help/search" element={<HelpCenterSearch />} />
                        <Route path="/help/category/:slug" element={<HelpCenterCategory />} />
                        <Route path="/help/article/:slug" element={<HelpCenterArticle />} />
                        <Route path="/docs/architecture" element={<DocsAccessRoute docKey="architecture"><ArchitectureDocs /></DocsAccessRoute>} />
                        <Route path="/docs/features" element={<DocsAccessRoute docKey="features"><FeaturesDoc /></DocsAccessRoute>} />
                        <Route path="/docs/qa" element={<DocsAccessRoute docKey="qa"><QADocs /></DocsAccessRoute>} />
                        <Route path="*" element={<NotFound />} />
                      </Routes>
                    </MaintenanceWrapper>
                  } />
                </Routes>
              </Suspense>
              {shouldLoadSupportChatbot && <RouteScopedSupportChatbot />}
              <Suspense fallback={null}>
                <LocationPrompt />
                <ManualCitySelector />
              </Suspense>
              </BrowserRouter>
              <CookieConsent />
              
              {/* Development Tools - Disabled */}
            </TooltipProvider>
            </LocationProvider>
          </CurrencyProvider>
        </AuthProvider>
      </QueryClientProvider>
    </ErrorBoundaryDev>
  );
};

export default App;
